import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, FileText } from 'lucide-react';
import SEO from '@/components/SEO';

export const TermsOfServicePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Terms of Service"
        description="Read the Terms of Service for BMI Calc Hub. Understand your rights and responsibilities when using our BMI calculator."
        canonical="/terms"
      />
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Calculator
          </Button>
        </Link>

        <Card className="card-elevated">
          <CardContent className="p-8 sm:p-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Terms of Service</h1>
                <p className="text-sm text-muted-foreground">Last updated: January 2025</p>
              </div>
            </div>

            <div className="prose prose-sm max-w-none text-muted-foreground space-y-6">
              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">1. Acceptance of Terms</h2>
                <p>
                  By accessing and using BMI Calc Hub (bmicalchub.com), you agree to be bound by these Terms of Service. 
                  If you do not agree to these terms, please do not use our website or services.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">2. Description of Service</h2>
                <p>
                  BMI Calc Hub provides a free online Body Mass Index (BMI) calculator with additional health metrics 
                  and athlete-focused features. Our services include:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>BMI calculation and categorization</li>
                  <li>Athlete mode with performance-based interpretations</li>
                  <li>Waist-to-height ratio calculations</li>
                  <li>Historical tracking of calculations</li>
                  <li>Optional user accounts for cloud-based data storage</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">3. User Accounts</h2>
                
                <h3 className="text-lg font-medium text-foreground mt-6 mb-3">3.1 Account Creation</h3>
                <p>
                  You may create an account to access additional features. You are responsible for maintaining 
                  the confidentiality of your account credentials and for all activities under your account.
                </p>

                <h3 className="text-lg font-medium text-foreground mt-6 mb-3">3.2 Account Requirements</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>You must provide accurate and complete information</li>
                  <li>You must be at least 13 years old to create an account</li>
                  <li>You may not share your account with others</li>
                  <li>You must notify us immediately of any unauthorized access</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">4. Acceptable Use</h2>
                <p>You agree NOT to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Use the service for any illegal purpose</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Interfere with or disrupt the service</li>
                  <li>Upload malicious code or content</li>
                  <li>Scrape or collect data without permission</li>
                  <li>Impersonate others or provide false information</li>
                  <li>Use the service to provide medical advice to others</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">5. Intellectual Property</h2>
                <p>
                  All content, features, and functionality of BMI Calc Hub, including but not limited to text, 
                  graphics, logos, and software, are owned by us and protected by intellectual property laws. 
                  You may not reproduce, distribute, or create derivative works without our express permission.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">6. Disclaimer of Warranties</h2>
                <p>
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER 
                  EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>The service will be uninterrupted or error-free</li>
                  <li>Results will be accurate or reliable</li>
                  <li>The service will meet your specific requirements</li>
                  <li>Any errors will be corrected</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">7. Limitation of Liability</h2>
                <p>
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, BMI CALC HUB SHALL NOT BE LIABLE FOR ANY INDIRECT, 
                  INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Loss of profits or data</li>
                  <li>Personal injury or health consequences</li>
                  <li>Errors or omissions in content</li>
                  <li>Any unauthorized access to your data</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">8. Health Information Disclaimer</h2>
                <p>
                  BMI Calc Hub provides general health information for educational purposes only. Our service:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Is NOT a substitute for professional medical advice</li>
                  <li>Should NOT be used for diagnosis or treatment</li>
                  <li>Does NOT create a doctor-patient relationship</li>
                  <li>May not account for individual health conditions</li>
                </ul>
                <p className="mt-4">
                  Always consult with a qualified healthcare provider before making health-related decisions.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">9. Third-Party Links</h2>
                <p>
                  Our website may contain links to third-party websites. We are not responsible for the content, 
                  privacy policies, or practices of any third-party sites. Accessing these links is at your own risk.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">10. Termination</h2>
                <p>
                  We reserve the right to terminate or suspend your access to the service at any time, without 
                  notice, for any reason, including breach of these Terms. Upon termination, your right to use 
                  the service will immediately cease.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">11. Changes to Terms</h2>
                <p>
                  We may modify these Terms at any time. Continued use of the service after changes constitutes 
                  acceptance of the modified terms. We encourage you to review these Terms periodically.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">12. Governing Law</h2>
                <p>
                  These Terms shall be governed by and construed in accordance with applicable laws, without 
                  regard to conflict of law principles.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">13. Contact Information</h2>
                <p>
                  For questions about these Terms of Service, please contact us at:
                </p>
                <p className="mt-2">
                  <strong>Email:</strong> info@bmicalchub.com<br />
                  <strong>Website:</strong> bmicalchub.com
                </p>
              </section>
            </div>
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default TermsOfServicePage;
